import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import Highlighter from "react-highlight-words";
import 'react-bootstrap-typeahead/css/Typeahead.css';
import Badge from 'react-bootstrap/Badge';

const SEARCH_URI = '/api/zoho/search-contacts';

export default React.forwardRef(function ZohoCustomerSearch({ onSelect, includeLeads = false, ...rest }, ref) {
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const abortControllerRef = useRef(null); // Ref to hold the AbortController

    const handleSearch = useCallback(async (query) => {
        // Cancel previous request if it's still running
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        // Create a new AbortController for the new request
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setIsLoading(true);
        try {
            let url = `${SEARCH_URI}?query=${query}`;
            if (includeLeads) {
                url += '&includeLeads=true';
            }
            const response = await fetch(url, { signal }); // Pass signal to fetch
            const items = await response.json();
            // Filter items to ensure they have a valid Email string
            const validItems = items.filter(item => typeof item.Email === 'string' && item.Email.length > 0);
            // Check if the request was aborted before setting state
            if (!signal.aborted) {
                setOptions(validItems);
            }
        } catch (error) {
            // Ignore abort errors, log others
            if (error.name !== 'AbortError') {
                console.error("Error fetching Zoho users:", error);
                // Check if the request was aborted before setting state
                if (!signal.aborted) {
                    setOptions([]); // Clear options on error
                }
            }
        } finally {
            // Check if the request was aborted before setting state
            if (!signal.aborted) {
                setIsLoading(false);
            }
        }
    }, []);

    // Cleanup function to abort fetch on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const filterBy = () => true;

    return (
        <AsyncTypeahead
            ref={ref}
            id="zoho-customer-search"
            isLoading={isLoading}
            options={options}
            labelKey="Email"
            minLength={2} // Trigger search only after 2 characters
            onSearch={handleSearch}
            onChange={_ => onSelect(_[0])}
            placeholder={includeLeads ? "Search by phone or email for Zoho customers and leads..." : "Search by phone or email for Zoho customers..." }
            filterBy={filterBy}
            renderMenuItemChildren={(contact, { text }) => {
                // Construct a simple address string
                const addressParts = [
                    contact.Delivery_Street,
                    contact.Delivery_City,
                    contact.Delivery_State,
                    contact.Delivery_Postcode,
                    contact.Delivery_Country
                ].filter(part => part);
                const address = addressParts.join(', ');

                return (
                    <>
                        <div>
                            {contact.Type === 'contact' && <Badge variant="secondary">Contact</Badge>}
                            {contact.Type === 'lead' && <Badge variant="success">Lead</Badge>}
                            &nbsp;
                            <Highlighter
                                highlightClassName='rbt-highlight-text'
                                searchWords={text.split(' ')}
                                autoEscape={true}
                                textToHighlight={contact.Email || ''}
                            />
                        </div>
                        <div style={{ color: '#666', marginBottom: 12 }}>
                            <small>
                                <div>
                                    <Highlighter
                                        highlightClassName='rbt-highlight-text'
                                        searchWords={text.split(' ')}
                                        autoEscape={true}
                                        textToHighlight={contact.Full_Name || `${contact.First_Name || ''} ${contact.Last_Name || ''}`.trim()}
                                    />
                                    {(contact.Account_Name || contact.Company) && (
                                        <> (
                                            <Highlighter
                                                highlightClassName='rbt-highlight-text'
                                                searchWords={text.split(' ')}
                                                autoEscape={true}
                                                textToHighlight={contact.Account_Name || contact.Company}
                                            />)
                                        </>
                                    )}
                                </div>
                                {(contact.Phone || contact.Mobile) && (
                                    <div>
                                        {contact.Mobile && (
                                            <Highlighter
                                                highlightClassName='rbt-highlight-text'
                                                searchWords={text.split(' ')}
                                                autoEscape={true}
                                                textToHighlight={contact.Mobile}
                                            />
                                        )}
                                        {contact.Phone && contact.Mobile && ' / '}
                                        {contact.Phone && (
                                            <Highlighter
                                                highlightClassName='rbt-highlight-text'
                                                searchWords={text.split(' ')}
                                                autoEscape={true}
                                                textToHighlight={contact.Phone}
                                            />
                                        )}
                                    </div>
                                )}
                                <div>{address}</div>
                            </small>
                        </div>
                    </>
                );
            }}
            {...rest}
            useCache={false}
        />
    );
});